import { scaleLinear, scaleTime, ScaleTime } from 'd3-scale';
import { debounce, isFinite } from 'lodash';
import getClosestTideDataPoint from 'utils/getClosestTide';
import { convertToMilliseconds, formatTimestampInTimezone, oneMinute, oneSecond } from 'utils/time';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConditionData } from 'types/conditionRating';
import type { ConsistencyData } from 'types/consistency';
import type {
  ConsistencyTooltipData,
  GraphDataError,
  GraphDataPointType,
  GraphDataPoints,
  GraphTooltipData,
  RatingTooltipData,
  SurfTooltipData,
  SwellSpectraTooltipData,
  SwellsTooltipData,
  TideTooltipData,
  TooltipDataPoint,
  WaveEnergyTooltipData,
  WindTooltipData,
} from 'types/graphs';
import type { LiveWindPoint } from 'types/liveWind';
import type { SurfData } from 'types/surf';
import type { SwellsData } from 'types/swell';
import type { TideData } from 'types/tide';
import type { WindData } from 'types/wind';
import type { SwellSpectraData } from 'types/spectra';
import type { WaveEnergyGraphData } from 'types/waveEnergy';
import {
  useActiveSpectraDayIndex,
  useActiveSpectraHourIndex,
  useActiveSpectraTimestamp,
  useActiveSpectraTimeLabel,
  useActiveSpectraLeftPosition,
  useActiveSpectraLeftNowPosition,
} from 'utils/globalGraphState/globalSwellSpectraState';
import { type GraphId } from '../GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';

export interface TooltipDataProps {
  consistency?: ConsistencyData;
  daysToDisplay: Array<number>;
  initialXPercentage: number;
  isGraphFullBleed: boolean;
  liveWind?: Array<LiveWindPoint>;
  ratings?: ConditionData;
  surf?: SurfData;
  swellSpectra?: SwellSpectraData;
  swells?: SwellsData;
  tide?: TideData;
  waveEnergy?: WaveEnergyGraphData;
  wind?: WindData;
  xScale: ScaleTime<number, number> | null;
}

const DAY_PADDING = 1;
const DEFAULT_LEFT_NOW_POSITIONS = {
  consistency: -1,
  energy: -1,
  rating: -1,
  spectra: -1,
  surf: -1,
  swells: -1,
  tide: -1,
  wind: -1,
};

const useTooltipData = ({
  consistency,
  daysToDisplay,
  initialXPercentage,
  isGraphFullBleed,
  liveWind,
  ratings,
  surf,
  swellSpectra,
  swells,
  tide,
  waveEnergy,
  wind,
  xScale,
}: TooltipDataProps): GraphTooltipData => {
  const currentUnixTimestamp = useMemo(() => Math.floor(+new Date() / 1000), []);
  const flatTideData = useMemo(() => tide?.days?.flat(1) ?? [], [tide?.days]);
  const [xPercentage, setXPercentage] = useState(-1);
  const [recalcNowPosition, setRecalcNowPosition] = useState(false);
  const [leftNowPositions, setLeftNowPositions] = useState(DEFAULT_LEFT_NOW_POSITIONS);
  const graphIdScrubbing = useRef('');
  const [, setSpectraDayIndex] = useActiveSpectraDayIndex();
  const [, setSpectraHourIndex] = useActiveSpectraHourIndex();
  const [, setSpectraTimestamp] = useActiveSpectraTimestamp();
  const [, setSpectraTimeLabel] = useActiveSpectraTimeLabel();
  const [, setSpectraLeftPosition] = useActiveSpectraLeftPosition();
  const [, setSpectraLeftNowPosition] = useActiveSpectraLeftNowPosition();

  useEffect(() => {
    if (initialXPercentage >= 0) setXPercentage(initialXPercentage);
  }, [initialXPercentage]);

  const setRecalcNowPositionDebounced = useMemo(
    () =>
      debounce(
        () => {
          setRecalcNowPosition(true);
        },
        50,
        { leading: true },
      ),
    [],
  );

  useEffect(() => {
    setRecalcNowPositionDebounced();

    return () => {
      setRecalcNowPositionDebounced.cancel();
    };
  }, [
    daysToDisplay,
    setRecalcNowPositionDebounced,
    xScale,
    ratings?.loading,
    surf?.loading,
    swells?.loading,
    tide?.loading,
    waveEnergy?.loading,
    wind?.loading,
  ]);

  const getTimeLabel = useCallback(
    (timestamp: number, utcOffset: number, previousTimestamp?: number) => {
      const timeDifference = currentUnixTimestamp - timestamp;

      // Calculate the interval between data points
      const interval = previousTimestamp ? timestamp - previousTimestamp : 0;

      // If we have a valid interval, use half of it as the allowedTimeDifference
      // This ensures we show "Now" for the closest timestamp rather than jumping ahead
      const allowedTimeDifference = interval > 0 ? interval / 2 : (oneMinute * 5) / oneSecond;
      const isNow = timeDifference >= 0 && timeDifference < allowedTimeDifference;

      return isNow ? 'Now' : formatTimestampInTimezone(timestamp, utcOffset, 'h:mmaaa');
    },
    [currentUnixTimestamp],
  );

  // Memoize the calculation of day index and hour index based on xPercentage
  const calculateIndices = useCallback(
    (customXPercentage?: number, isLiveWind?: boolean) => {
      const xPercentageForDataPoint = customXPercentage ?? xPercentage;
      const dayIndex = isLiveWind ? 0 : Math.floor(xPercentageForDataPoint * daysToDisplay.length);
      const percentPerDay = 1 / daysToDisplay.length;
      const percentBeforeDay = percentPerDay * dayIndex;
      const xPercentageForDay = (xPercentageForDataPoint - percentBeforeDay) * daysToDisplay.length;

      return {
        dayIndex,
        xPercentageForDay,
        xPercentageForDataPoint,
      };
    },
    [daysToDisplay.length, xPercentage],
  );

  // Memoize the scale creation for a specific day
  const createDayScales = useCallback(
    (
      dayIndex: number,
      numDataPointsInDay: number,
      graphWidth: number,
      isTide: boolean,
      isLiveWind: boolean,
    ) => {
      const graphDayWidth = graphWidth / daysToDisplay.length - 1;
      const startRange = (graphDayWidth + DAY_PADDING) * dayIndex;
      const endRange = startRange + graphDayWidth + DAY_PADDING;

      const xScaleForDay =
        !isTide && !isLiveWind
          ? scaleLinear().range([startRange, endRange]).domain([0, numDataPointsInDay])
          : null;

      const xScaleForDayTide =
        isTide && tide?.days?.[daysToDisplay[dayIndex]]
          ? scaleTime()
              .range([startRange, endRange])
              .domain([
                convertToMilliseconds(tide.days[daysToDisplay[dayIndex]][0]?.timestamp ?? 0),
                convertToMilliseconds(
                  tide.days[daysToDisplay[dayIndex]][tide.days[daysToDisplay[dayIndex]].length - 1]
                    ?.timestamp ?? 0,
                ),
              ])
          : null;

      return {
        graphDayWidth,
        startRange,
        endRange,
        xScaleForDay,
        xScaleForDayTide,
      };
    },
    [daysToDisplay, tide?.days],
  );

  const getClosestDataPoint = useCallback(
    (
      data: TooltipDataPoint,
      dataPointType: GraphDataPointType,
      error: GraphDataError,
      loading: boolean,
      customXPercentage?: number,
      isLiveWind?: boolean,
      matchTimestamp?: number,
    ) => {
      if (error || loading || !xScale) {
        return {
          data: null,
          dayIndex: -1,
          error,
          hourIndex: -1,
          leftPosition: null,
          leftDayPosition: null,
          loading,
          timeLabel: null,
          timestamp: null,
          utcOffset: null,
        };
      }

      const daysInView = ((data || []) as GraphDataPoints).filter(
        (_day, index) => daysToDisplay.indexOf(index) >= 0,
      );

      const { dayIndex, xPercentageForDay, xPercentageForDataPoint } = calculateIndices(
        customXPercentage,
        isLiveWind,
      );

      const range = xScale.range();
      let graphWidth = range && range.length === 2 ? range[1] - range[0] : 1;
      if (customXPercentage && isLiveWind) {
        graphWidth /= daysToDisplay.length;
      }

      const isTide = dataPointType === 'tide';
      const numDataPointsInDay = daysInView?.[dayIndex]?.length || 0;
      const hourIndex = Math.floor(
        (isLiveWind ? xPercentageForDataPoint : xPercentageForDay) * numDataPointsInDay,
      );
      const previousTimestamp =
        !isTide && hourIndex > 0 ? daysInView?.[dayIndex]?.[hourIndex - 1]?.timestamp : undefined;

      const shouldInterpolateTide =
        graphIdScrubbing?.current === 'tide' ||
        (graphIdScrubbing?.current === 'wind' &&
          !!liveWind?.length &&
          daysToDisplay[dayIndex] === 0);

      const { graphDayWidth, startRange, endRange, xScaleForDay, xScaleForDayTide } =
        createDayScales(dayIndex, numDataPointsInDay, graphWidth, isTide, !!isLiveWind);

      const dataPoint = isTide
        ? getClosestTideDataPoint(
            flatTideData,
            shouldInterpolateTide,
            (xScaleForDayTide
              ?.invert(xPercentageForDay * (endRange - startRange) + startRange)
              ?.getTime() ?? NaN) / 1000,
            matchTimestamp,
          )
        : daysInView?.[dayIndex]?.[hourIndex] || null;

      const barWidth = graphDayWidth / numDataPointsInDay;

      const leftDayPositionOffset = isGraphFullBleed && dayIndex > 0 ? -40 : 0;
      const leftDayPosition = graphDayWidth * dayIndex + leftDayPositionOffset;
      const leftOffset = !isTide ? barWidth / 2 : 0;

      let leftPosition = null;

      if (isLiveWind) {
        // Still us scaleTime for Tide and Live Wind data tooltips since they have custom implementations.
        // Ie. The tide graph interpolates between data points, and the live wind graph has high resolution data for one day only.
        leftPosition = dataPoint?.timestamp
          ? Math.round((xScale(dataPoint.timestamp) + leftOffset) * 2) / 2
          : 0;
      } else if (isTide && !!xScaleForDayTide) {
        leftPosition = dataPoint?.timestamp
          ? Math.round(xScaleForDayTide(dataPoint.timestamp * 1000))
          : 0;
      } else {
        // Note: D3's scaleTime function has an issue when using unix timestamps and crossing DST.
        // This causes the tooltips to be misaligned and graph bars to be positioned incorrectly.
        // Even though we're dealing with time series data, it is actually linear and therefore we
        // can use scaleLinear and indexes instead of timestamps.
        // This implementation also allows for each day to have a different amount of data points without breaking the tooltip alignment.
        leftPosition =
          dataPoint?.timestamp && !!xScaleForDay
            ? Math.round((xScaleForDay(hourIndex) + leftOffset) * 2) / 2
            : 0;
      }

      const timeLabel =
        dataPoint && isFinite(dataPoint.timestamp)
          ? getTimeLabel(dataPoint.timestamp, dataPoint.utcOffset, previousTimestamp)
          : null;

      if (timeLabel === 'Now' && leftNowPositions?.[dataPointType] < 0 && isFinite(leftPosition)) {
        setLeftNowPositions({
          ...leftNowPositions,
          [dataPointType]: leftPosition,
        });
      }

      return {
        data:
          isLiveWind && dataPoint && (dataPoint as LiveWindPoint).wind
            ? {
                ...(dataPoint as LiveWindPoint).wind,
                isLiveCurrently:
                  isFinite((dataPoint as LiveWindPoint)?.wind?.speed) &&
                  (dataPoint as LiveWindPoint).sensorWind === true &&
                  (timeLabel === 'Now' ||
                    (dayIndex === 0 && dataPoint.timestamp < currentUnixTimestamp)),
              }
            : dataPoint,
        dayIndex,
        error,
        hourIndex,
        leftDayPosition,
        leftNowPosition: leftNowPositions?.[dataPointType],
        leftPosition,
        loading,
        timeLabel,
        timestamp: dataPoint?.timestamp,
        utcOffset: dataPoint?.utcOffset,
        isNow: timeLabel === 'Now',
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      calculateIndices,
      createDayScales,
      daysToDisplay,
      flatTideData,
      getTimeLabel,
      isGraphFullBleed,
      leftNowPositions,
      liveWind?.length,
      xScale,
    ],
  );

  const getClosestWindDataPoint = useCallback(
    (
      data: TooltipDataPoint,
      error: GraphDataError,
      loading: boolean,
      customXPercentage?: number,
    ) => {
      const adjustedXPercentage = (customXPercentage ?? xPercentage) * daysToDisplay.length;
      const dayIndex = Math.floor(adjustedXPercentage);
      const isLiveWind = !!liveWind?.length && daysToDisplay[dayIndex] === 0;
      if (isLiveWind) {
        return getClosestDataPoint(
          [liveWind],
          'wind',
          error,
          isLiveWind ? false : loading,
          adjustedXPercentage,
          true,
        );
      }

      return getClosestDataPoint(data, 'wind', error, loading, customXPercentage);
    },
    [daysToDisplay, getClosestDataPoint, liveWind, xPercentage],
  );

  const getDataPoint = useCallback(
    (dataPointType: GraphDataPointType, customXPercentage?: number, matchTimestamp?: number) => {
      switch (dataPointType) {
        case 'energy': {
          const hasDisplayableWaveEnergySteps =
            waveEnergy !== undefined &&
            !waveEnergy.loading &&
            !waveEnergy.error &&
            !!waveEnergy.days &&
            daysToDisplay.flatMap((day) => waveEnergy.days?.[day] ?? []).length > 0;

          if (hasDisplayableWaveEnergySteps && waveEnergy) {
            return getClosestDataPoint(
              waveEnergy.days ?? [],
              dataPointType,
              waveEnergy.error,
              waveEnergy.loading,
              customXPercentage,
            );
          }
          return swells
            ? getClosestDataPoint(
                swells.days,
                dataPointType,
                swells.error,
                swells.loading,
                customXPercentage,
              )
            : undefined;
        }
        case 'rating':
          return getClosestDataPoint(
            ratings?.days || [],
            dataPointType,
            ratings?.error || null,
            ratings?.loading || false,
            customXPercentage,
          ) as RatingTooltipData;
        case 'tide':
          return tide
            ? (getClosestDataPoint(
                tide.days,
                dataPointType,
                tide.error,
                tide.loading,
                customXPercentage,
                false,
                matchTimestamp,
              ) as TideTooltipData)
            : undefined;
        case 'surf':
          return surf
            ? (getClosestDataPoint(
                surf.days,
                dataPointType,
                surf.error,
                surf.loading,
                customXPercentage,
              ) as SurfTooltipData)
            : undefined;
        case 'swells':
          return swells
            ? (getClosestDataPoint(
                swells.days,
                dataPointType,
                swells.error,
                swells.loading,
                customXPercentage,
              ) as SwellsTooltipData)
            : undefined;
        case 'consistency':
          return consistency
            ? (getClosestDataPoint(
                consistency?.days,
                dataPointType,
                consistency?.error,
                consistency?.loading,
                customXPercentage,
              ) as ConsistencyTooltipData)
            : undefined;
        case 'wind':
          return wind
            ? (getClosestWindDataPoint(
                wind.days,
                wind.error,
                wind.loading,
                customXPercentage,
              ) as WindTooltipData)
            : undefined;
        default:
          return null;
      }
    },
    [
      consistency,
      daysToDisplay,
      getClosestDataPoint,
      getClosestWindDataPoint,
      ratings,
      surf,
      swells,
      tide,
      waveEnergy,
      wind,
    ],
  );

  // Define setGraphIdScrubbing function
  const setGraphIdScrubbing = useCallback((id: string) => {
    graphIdScrubbing.current = id;
  }, []);

  // Memoize the updateNowPositions function to prevent recreating it on each render
  const updateNowPositions = useCallback(() => {
    const newLeftNowPositions = {
      ...DEFAULT_LEFT_NOW_POSITIONS,
      consistency: getDataPoint('consistency', initialXPercentage)?.leftPosition ?? -1,
      energy: getDataPoint('energy', initialXPercentage)?.leftPosition ?? -1,
      rating: getDataPoint('rating', initialXPercentage)?.leftPosition ?? -1,
      spectra: getDataPoint('spectra', initialXPercentage)?.leftPosition ?? -1,
      surf: getDataPoint('surf', initialXPercentage)?.leftPosition ?? -1,
      swells: getDataPoint('swells', initialXPercentage)?.leftPosition ?? -1,
      tide: getDataPoint('tide', initialXPercentage)?.leftPosition ?? -1,
      wind: getDataPoint('wind', initialXPercentage)?.leftPosition ?? -1,
    };

    setLeftNowPositions(newLeftNowPositions);
    setRecalcNowPosition(false);
  }, [initialXPercentage, getDataPoint]);

  useEffect(() => {
    if (recalcNowPosition && daysToDisplay?.length) {
      updateNowPositions();
    }
  }, [daysToDisplay, recalcNowPosition, updateNowPositions]);

  const renderTooltip = useCallback((newXPercentage: number, graphId: GraphId) => {
    setXPercentage(newXPercentage);

    if (graphIdScrubbing?.current !== null) {
      graphIdScrubbing.current = graphId;
    }
  }, []);

  // Replace the problematic useMemo with a useEffect to prevent state updates during render
  useEffect(() => {
    if (swellSpectra) {
      const spectraDataPoint = getClosestDataPoint(
        swellSpectra.days,
        'spectra',
        swellSpectra.error,
        swellSpectra.loading,
        undefined,
      ) as SwellSpectraTooltipData;

      setSpectraDayIndex(spectraDataPoint?.dayIndex);
      setSpectraHourIndex(spectraDataPoint?.hourIndex);
      setSpectraTimestamp(spectraDataPoint?.timestamp as number);
      setSpectraTimeLabel(spectraDataPoint?.timeLabel);
      setSpectraLeftPosition(spectraDataPoint?.leftPosition);
      setSpectraLeftNowPosition(spectraDataPoint?.leftNowPosition);
    }
  }, [
    getClosestDataPoint,
    setSpectraDayIndex,
    setSpectraHourIndex,
    setSpectraLeftNowPosition,
    setSpectraLeftPosition,
    setSpectraTimeLabel,
    setSpectraTimestamp,
    swellSpectra,
    xPercentage,
  ]);

  // Memoize the result object to prevent recreating it on each render
  return useMemo(() => {
    // Helper function to get data points with proper type casting
    const getTypedDataPoint = <T>(type: GraphDataPointType): T => getDataPoint(type) as T;

    // Get data points with proper type casting
    const consistencyData = getTypedDataPoint<ConsistencyTooltipData>('consistency');
    const ratingData = getTypedDataPoint<RatingTooltipData>('rating');
    const surfData = getTypedDataPoint<SurfTooltipData>('surf');
    const swellsData = getTypedDataPoint<SwellsTooltipData>('swells');
    const tideData = getTypedDataPoint<TideTooltipData>('tide');
    const windData = getTypedDataPoint<WindTooltipData>('wind');
    const spectraData = getTypedDataPoint<SwellSpectraTooltipData>('spectra');
    const energyData = getTypedDataPoint<WaveEnergyTooltipData | SwellsTooltipData>('energy');

    return {
      consistency: consistencyData,
      energy: energyData,
      graphIdScrubbing: graphIdScrubbing.current,
      leftNowPositions,
      rating: ratingData,
      renderTooltip,
      setGraphIdScrubbing,
      spectra: spectraData,
      surf: surfData,
      swells: swellsData,
      tide: tideData,
      wind: windData,
      xPercentage,
    };
  }, [getDataPoint, leftNowPositions, renderTooltip, setGraphIdScrubbing, xPercentage]);
};

export default useTooltipData;
